import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { useSMPData } from './hooks/useSMPData';
import {
  useGetOrderHistory,
  useGetSkippedEvents,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type { SMPOrder, SMPSkipEvent } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { formatSMPDate } from './utils/smpHelpers';
import { SMP_ORDER_HISTORY_DETAILS_PATH } from '@/constants/paths';

interface DisplayOrder {
  id: number;
  productName: string;
  productImage?: string;
  date: string;
  dateLabel: string;
  isSkipped: boolean;
}

/**
 * Merge skipped events into transaction history, matching the logic from
 * shared-fe/src/redux/sagas/subscriptions.js generateTransactionHistory().
 */
const buildOrderList = (
  orders: SMPOrder[],
  skippedEvents: SMPSkipEvent[],
): DisplayOrder[] => {
  const realOrders: DisplayOrder[] = orders.map((order) => {
    const firstItem = order.line_items?.[0];
    const productName =
      firstItem?.title || firstItem?.shopify_product?.title || 'Subscription Order';
    const productImage = firstItem?.shopify_product?.image;

    const deliveryDate = order.lacore_order?.delivery_date;
    const displayDate = deliveryDate || order.order_date;
    const dateLabel = deliveryDate ? 'Delivered' : 'Order placed on';

    return {
      id: order.id,
      productName,
      productImage,
      date: displayDate,
      dateLabel,
      isSkipped: false,
    };
  });

  const now = new Date();
  const skippedOrders: DisplayOrder[] = [];
  const seenDates = new Set<string>();

  for (const event of skippedEvents) {
    try {
      const state = JSON.parse(event.event_state);
      const skippedDate: string = state.skipped_date;
      if (!skippedDate || seenDates.has(skippedDate)) continue;
      seenDates.add(skippedDate);

      if (new Date(skippedDate) >= now) continue;

      skippedOrders.push({
        id: event.id,
        productName: 'Subscription Order',
        date: skippedDate,
        dateLabel: 'Order date',
        isSkipped: true,
      });
    } catch {
      // skip malformed event_state
    }
  }

  const allOrders = [...realOrders, ...skippedOrders];
  allOrders.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return allOrders;
};

export const SMPOrderHistoryPage = () => {
  const navigate = useNavigate();
  const {
    subscriptionId,
    isSessionReady,
    isLoading: isSMPLoading,
    isError: isSMPError,
  } = useSMPData({
    skip: { discounts: true, changePlan: true, uiMessages: true, canSkip: true, holdingPattern: true },
  });

  const {
    data: orders,
    isLoading: isLoadingOrders,
    isError: isErrorOrders,
  } = useGetOrderHistory(subscriptionId, {
    enabled: isSessionReady && !!subscriptionId,
  });

  const { data: skippedEvents } = useGetSkippedEvents(subscriptionId, {
    enabled: isSessionReady && !!subscriptionId,
  });

  const isLoading = isSMPLoading || isLoadingOrders;
  const isError = isSMPError || isErrorOrders;

  const displayOrders = useMemo(
    () => buildOrderList(orders ?? [], skippedEvents ?? []),
    [orders, skippedEvents],
  );

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <Skeleton className="h-8 w-48" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  const handleOrderClick = (order: DisplayOrder) => {
    if (order.isSkipped) return;
    navigate(SMP_ORDER_HISTORY_DETAILS_PATH.replace(':orderId', String(order.id)));
  };

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-orderHistoryPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <h1 className="typography-h6 text-primary pt-5">Order Details</h1>
      </div>

      <div className="flex flex-col">
        {displayOrders.length > 0 ? (
          displayOrders.map((order) => (
            <button
              key={`${order.id}-${order.isSkipped ? 'skipped' : 'order'}`}
              type="button"
              disabled={order.isSkipped}
              onClick={() => handleOrderClick(order)}
              className="flex items-center justify-between py-5 border-b border-stroke-secondary-subtle text-left disabled:cursor-default"
            >
              <div className="flex flex-col gap-3 flex-1 min-w-0">
                <span className="typography-body text-primary">
                  {order.productName}
                </span>
                <span className="typography-caption1 text-tertiary">
                  {order.dateLabel}: {formatSMPDate(order.date)}
                </span>
                {order.isSkipped && (
                  <span className="typography-caption1 text-warning">
                    Order Skipped
                  </span>
                )}
              </div>
              {!order.isSkipped && (
                <ChevronRightIcon
                  width={24}
                  height={24}
                  className="text-tertiary shrink-0 ml-4"
                />
              )}
            </button>
          ))
        ) : (
          <div className="py-5">
            <span className="typography-body text-secondary">
              No order history available.
            </span>
          </div>
        )}
      </div>
    </section>
  );
};
